<template>
  <div class="page join-landing">
    <AppTopHeader />
    <main class="container join-main">
      <div v-if="loading" class="join-card">
        <p>Loading invitation…</p>
      </div>
      <div v-else-if="error" class="join-card join-error">
        <h1>Invitation unavailable</h1>
        <p>{{ error }}</p>
        <router-link to="/" class="link-home">Back to home</router-link>
      </div>
      <div v-else class="join-card join-ok">
        <p class="join-kicker">Emergency contact — Nomade Digital</p>
        <h1>{{ preview.journeyTitle || 'Trip' }}</h1>
        <p class="join-desc">
          <template v-if="preview.sharerName">{{ preview.sharerName }} invited you</template>
          <template v-else>You've been invited</template>
          as an <strong>emergency contact</strong> for this trip. Open the app to accept
          (while signed in) and follow the accommodations, transport and the path in real time.
        </p>
        <a class="btn-open-app" :href="deepLink">Open in app</a>
        <p v-if="preview.appStoreUrl" class="join-store">
          <a :href="preview.appStoreUrl" rel="noopener noreferrer" target="_blank">App Store (iOS)</a>
          <span v-if="preview.playStoreUrl"> · </span>
          <a
            v-if="preview.playStoreUrl"
            :href="preview.playStoreUrl"
            rel="noopener noreferrer"
            target="_blank"
          >Google Play</a>
        </p>
        <p class="join-hint">
          On iPhone, Safari may not open the app automatically from an <code>https://</code> link.
          Use the <strong>Open in app</strong> button above. If you're on TestFlight, make sure the app is installed.
        </p>
        <p class="join-note">
          The emergency contact has read-only access to accommodations, transport and the path.
          This feature does not replace an emergency service — in a real emergency, call your local emergency number (911 / 112).
        </p>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios';
import AppTopHeader from '@/components/AppTopHeader.vue';
import { flipmemoApiPath } from '@/config/flipmemoApi';

export default {
  name: 'EmergencyContactLandingPage',
  components: { AppTopHeader },
  data() {
    return {
      loading: true,
      error: null,
      preview: {},
    };
  },
  computed: {
    token() {
      return String(this.$route.params.token || '').trim();
    },
    deepLink() {
      // Sem encodeURIComponent: o token é base64url; path segment único (igual à app).
      return `flipmemo://emergency/${this.token}`;
    },
  },
  async mounted() {
    const t = this.token;
    if (!t) {
      this.error = 'Invalid invitation link.';
      this.loading = false;
      return;
    }
    try {
      const { data } = await axios.get(flipmemoApiPath(`/emergency/${encodeURIComponent(t)}`), {
        headers: { Accept: 'application/json' },
      });
      this.preview = data || {};
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) {
        this.error = 'This invitation no longer exists or has expired.';
      } else {
        this.error = 'We couldn\'t load the invitation. Please try again later.';
      }
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style scoped>
.join-landing {
  min-height: 100vh;
  background: #f8fafc;
  /* AppTopHeader é position:fixed com .topbar-inner height 66px */
  padding-top: calc(66px + env(safe-area-inset-top, 0px) + 16px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  box-sizing: border-box;
}
.join-main {
  padding: 0 1rem 2.5rem;
  max-width: 480px;
  margin: 0 auto;
}
.join-card {
  background: #fff;
  border-radius: 16px;
  padding: 1.75rem 1.5rem;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.08);
}
.join-error h1 {
  font-size: 1.35rem;
  margin: 0 0 0.75rem;
}
.join-kicker {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin: 0 0 0.5rem;
}
.join-ok h1 {
  font-size: 1.5rem;
  margin: 0 0 1rem;
  line-height: 1.25;
}
.join-desc {
  color: #475569;
  line-height: 1.5;
  margin: 0 0 1.25rem;
}
.btn-open-app {
  display: inline-block;
  padding: 0.85rem 1.35rem;
  background: #1a6b5c;
  color: #fff !important;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 600;
}
.btn-open-app:hover {
  filter: brightness(1.05);
}
.join-store {
  margin: 1.25rem 0 0;
  font-size: 0.95rem;
}
.join-hint {
  margin: 1.5rem 0 0;
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.45;
}
.join-hint code {
  font-size: 0.8em;
  background: #f1f5f9;
  padding: 0.1em 0.35em;
  border-radius: 4px;
}
.join-note {
  margin: 1rem 0 0;
  font-size: 0.8rem;
  color: #94a3b8;
  line-height: 1.45;
}
.link-home {
  display: inline-block;
  margin-top: 1rem;
}
</style>
